import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../utils/cn.ts';
import { Icon } from '../../components/common/Icon.tsx';

export interface SortableRowProps {
    /** Unique ID for this row (used by dnd-kit) */
    id: string;
    /** Domain-specific <td> elements */
    children: React.ReactNode;
    /** Actions slot — rendered in the rightmost column (delete, duplicate, etc.) */
    actions?: React.ReactNode;
    /** Optional data attributes to spread on the <tr> */
    dataAttributes?: Record<string, string>;
    /** Optional focus handler — e.g. for setting active scene */
    onFocus?: () => void;
    /** Extra className on the <tr> */
    className?: string;
    /** Hide drag handle (e.g. in read-only mode). Default: false */
    hideDragHandle?: boolean;
    /** Optional dnd-kit data payload */
    sortableData?: Record<string, unknown>;
}

/**
 * SortableRow — Shared drag-sortable table row shell.
 *
 * Renders a `<tr>` with:
 *   1. A drag handle `<td>` (optional, hideable)
 *   2. `children` — domain-specific `<td>` columns
 *   3. `actions` — rightmost column for delete / duplicate buttons
 *
 * Consumers apply `handleTableNavigation` / `handleLastRowTab` from
 * `useTableEffects.ts` on their own `<td>` `onKeyDown` handlers.
 *
 * Usage:
 * ```tsx
 * <SortableRow id={item.id} actions={<DeleteButton />}>
 *   <td>...</td>
 *   <td>...</td>
 * </SortableRow>
 * ```
 */
export const SortableRow = React.memo<SortableRowProps>(({
    id,
    children,
    actions,
    dataAttributes = {},
    onFocus,
    className,
    hideDragHandle = false,
    sortableData,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id, data: sortableData });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative' as const,
        zIndex: isDragging ? 50 : 1,
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={cn(
                'group transition-colors hover:bg-blue-50 dark:hover:bg-primary/10',
                'row-item border-transparent border-l-2 hover:border-l-primary',
                isDragging && 'bg-primary/5 ring-2 ring-primary/20',
                className
            )}
            data-row-id={id}
            {...dataAttributes}
            onFocus={onFocus}
        >
            {/* Drag Handle */}
            {!hideDragHandle && (
                <td
                    className="handle-row w-8 touch-none px-2 py-3 text-center align-middle print:hidden"
                    {...attributes}
                    {...listeners}
                >
                    <Icon name="drag_handle" size="sm" className="drag-handle cursor-grab text-gray-400 opacity-0 active:cursor-grabbing group-hover:opacity-50" />
                </td>
            )}

            {/* Domain-specific columns */}
            {children}

            {/* Actions column */}
            {actions ? <td className="w-16 px-2 py-3 text-center align-middle print:hidden">
                    <div className="flex items-center justify-center gap-1">
                        {actions}
                    </div>
                </td> : null}
        </tr>
    );
});

SortableRow.displayName = 'SortableRow';
